// @ts-expect-error TS7016, tinymce has not yet been migrated.
import tinymce from 'tinymce';
import { getIsPropertyOnlyToken, getTagUseFromCategory } from 'rich-text-lib/utils/properties';
import { getPathsMap } from 'rich-text-lib/utils/buildProperties';
import { curry, compose } from 'rich-text-lib/utils/iterables';
import { addTokenTransforms, createTokenString, getTokenLabel, getTokenPattern, getTokenPatternWithDefault, getTokenProperties, renderTokenBase, splitHublVar } from './utils';
import { getHsDataTokenSettings } from './utils';
import { addPreProcessTransform, replaceNodes } from '../utils/transformer';
//Begin token single qoute escaping approach
//
// We need to insert a literal escape character in the case
// of a single qoute in the default value of a personalization token.
// This is the result of a limitation / condition of the backend ...
// see https://hubspot.slack.com/archives/C5GRAFS4A/p1583180373051600
// Because we only want to insert this once we want to use a "negative look behind"
// regex approach. However because JS does not support that strategy, we revers the
// string and use a negative lookahead finlly reversing the string again.
const stringReverse = string => string.split('').reverse().join('');
const fpReplace = curry((match, value, string) => string.replace(match, value));
const escapeUnescapedSingleQuote = compose(stringReverse, fpReplace(/'(?!\\)/, "'\\"), stringReverse);
//End token single qoute escaping approach

const createTokenLink = ({
  href,
  text,
  style
}) => {
  const anchor = document.createElement('a');
  anchor.setAttribute('href', href);
  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('style', style);
  anchor.textContent = text;
  return anchor;
};
const renderToken = dataTokenConfig => ({
  path,
  category = '',
  defaultValue,
  filter
}) => {
  const tagUse = getTagUseFromCategory(category);
  const [__category, property] = path.split('.');
  const isPropertyOnlyToken = getIsPropertyOnlyToken(category, property);

  // NOTE: Label could be undefined at first load in which case we just
  // render the raw token information while omitting [data-mce-poperty-label]
  // so that we know which tokens need to be adjusted once we have the labels
  const label = getTokenLabel(path, dataTokenConfig);
  const tokenText = label || path;
  const pathsMap = getPathsMap(dataTokenConfig);
  if (pathsMap && !pathsMap[path]) {
    return document.createTextNode(createTokenString({
      path,
      defaultValue: defaultValue ? escapeUnescapedSingleQuote(defaultValue) : defaultValue,
      filter
    }));
  }
  const hasDataSensitivity = pathsMap && pathsMap[path].hasDataSensitivity;
  const setTokenAttributes = token => {
    token.setAttribute('data-mce-hubspot-object', 'true');
    token.setAttribute('data-mce-token-path', path);
    token.setAttribute('data-mce-property-category', isPropertyOnlyToken ? 'property-only' : category);
    if (label) {
      token.setAttribute('data-mce-property-label', label);
    }
    if (defaultValue != null) {
      token.setAttribute('data-mce-property-default-value', defaultValue);
    }
    if (filter) {
      token.setAttribute('data-mce-filter', filter);
    }
    if (hasDataSensitivity) {
      token.setAttribute('data-mce-data-sensitivity', 'true');
    }
    token.classList.add(tagUse);
  };
  return renderTokenBase(tokenText, category, setTokenAttributes, dataTokenConfig.maxTokenWidth, hasDataSensitivity);
};

// We need this object for testing purposes
export const RenderTokenExport = {
  renderToken
};

/**
 *
 * @see MergeTagPlaceholderRewriter.java
 */
const getLegacyNodePreProcessTransform = dataTokenConfig => dom => {
  const linkTypes = ['unsubscribe', 'viewaswebpage', 'emailoptin'];
  const getLinkTypeForNode = node => {
    return linkTypes.find(type => node.hasAttribute(`data-${type}`));
  };
  const nodes = Array.from(dom.querySelectorAll('input.hubspot-mergetag'));
  replaceNodes(nodes, node => {
    if (node.hasAttribute('data-canspam')) {
      const properties = getTokenProperties(node.getAttribute('data-canspam') || '');
      if (properties) {
        const path = properties.category ? `${properties.category}.${properties.property}` : properties.property;
        return RenderTokenExport.renderToken(dataTokenConfig)(Object.assign({
          path
        }, properties));
      }
    } else if (getLinkTypeForNode(node)) {
      const type = getLinkTypeForNode(node);
      if (type) {
        return createTokenLink({
          text: node.getAttribute('value') || '',
          href: node.getAttribute(`data-${type}`) || '',
          style: node.getAttribute('data-style') || ''
        });
      }
    } else if (node.hasAttribute('value')) {
      const properties = splitHublVar(`contact.${node.getAttribute('value')}`);
      if (properties) {
        const path = properties.category ? `${properties.category}.${properties.property}` : properties.property;
        return RenderTokenExport.renderToken(dataTokenConfig)(Object.assign({
          path
        }, properties));
      }
    }
    return node;
  });
};
const replacePlaceholderWithHubl = () => node => {
  const pathAttr = node.getAttribute('data-mce-token-path') || '';
  const category = node.getAttribute('data-mce-property-category') || '';
  const property = node.getAttribute('data-mce-property-name') || '';
  const subProperty = node.getAttribute('data-mce-subproperty');
  const defaultValueAttr = node.getAttribute('data-mce-property-default-value');
  const filter = node.getAttribute('data-mce-filter') || '';
  const defaultValue = defaultValueAttr && tinymce.html.Entities.decode(defaultValueAttr);
  const isPropertyOnlyToken = getIsPropertyOnlyToken(category, property);
  const path = pathAttr || (isPropertyOnlyToken ? property : `${category}.${property}${subProperty ? `.${subProperty}` : ''}`);
  return document.createTextNode(createTokenString({
    path,
    defaultValue: defaultValue ? escapeUnescapedSingleQuote(defaultValue) : defaultValue,
    filter
  }));
};
const getTokenPreProcessTransform = dataTokenConfig => properties => {
  const renderedToken = RenderTokenExport.renderToken(dataTokenConfig)(properties);
  if (!renderedToken) {
    return null;
  }
  return renderedToken.nodeType === Node.ELEMENT_NODE ? renderedToken.outerHTML : renderedToken.textContent;
};
export const addTransforms = editor => {
  const dataTokenConfig = getHsDataTokenSettings(editor);
  if (!dataTokenConfig) {
    return;
  }
  let supportedCrmObjectFQNs = [];
  if (dataTokenConfig && dataTokenConfig.supportedCrmObjectFQNs) {
    supportedCrmObjectFQNs = dataTokenConfig.supportedCrmObjectFQNs;
  }

  // Preprocessor for legacy "merge tags"
  addPreProcessTransform(editor, getLegacyNodePreProcessTransform(dataTokenConfig));

  // Pre and Post transforms
  addTokenTransforms(editor, supportedCrmObjectFQNs, [{
    TOKEN_PATTERN: getTokenPattern(dataTokenConfig),
    USES_DEFAULT: false
  }, {
    TOKEN_PATTERN: getTokenPatternWithDefault(dataTokenConfig),
    USES_DEFAULT: true
  }], getTokenPreProcessTransform(dataTokenConfig), replacePlaceholderWithHubl());
};
export const getUpdateAlreadyRenderedToken = dataTokenConfig => token => {
  const path = token.getAttribute('data-mce-token-path') || '';
  const category = token.getAttribute('data-mce-property-category') || '';
  const filter = token.getAttribute('data-mce-filter') || '';
  const defaultValue = token.getAttribute('data-mce-property-default-value');
  const placeholderNode = RenderTokenExport.renderToken(dataTokenConfig)({
    path,
    category,
    defaultValue,
    filter
  });
  const parentNode = token.parentNode;
  if (parentNode && placeholderNode) {
    parentNode.replaceChild(placeholderNode, token);
  }
};

/**
 * Replace token values with translated labels
 * Updating already transformed tokens with translated values
 */
export const handleDataTokensLoaded = editor => {
  const dataTokenConfig = getHsDataTokenSettings(editor);
  if (!dataTokenConfig) {
    return;
  }
  const body = editor.getBody();
  if (body) {
    const alreadyRenderedTokens = Array.from(body.querySelectorAll('[data-mce-token-path]:not([data-mce-property-label]):not([data-mce-rss-link]):not([data-mce-property-category=post])'));
    const updateToken = getUpdateAlreadyRenderedToken(dataTokenConfig);
    alreadyRenderedTokens.forEach(updateToken);
  }
};